import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import entitiesHelper from "../../../../API/EntitiesAI/entitiesHelper";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import { isLargeScreen } from "../../../MainViewContainer";
import SettingsAI from "../SettingsAI";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";

const NERsList = () => {
  const formRef = useRef();
  const { currentTheme } = AppTheme.useTheme();
  const { height } = useWindowDimensions();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showNERModal, setShowNERModal] = React.useState(false);
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedNERId, setSelectedNERId] = React.useState(null);
  const [isLoading] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [displayedNERs, setDisplayedNERs] = React.useState([]);
  const [selectedNERIds, setSelectedNERIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const [initialMapValues, setInitialMapValues] = useState({
    label: "",
    description: "",
    emoji: "",
  });

  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.emoji"),
      width: 0.01,
    },
    {
      title: t("settings.list.label"),
      width: 0.3,
    },
    {
      title: t("settings.list.description"),
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];

  const ners = useMemo(() => {
    return state?.aiEntitiesConfig?.filter((i) => i.type === "ner") || [];
  }, [state?.aiEntitiesConfig]);

  useEffect(() => {
    const sortedNERs = [...ners].sort((a, b) =>
      a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
    );
    setDisplayedNERs(sortedNERs);
  }, [ners]);

  const onNERCreated = useCallback(
    (event) => {
      if (event === "success") {
        AppToasty.show(
          t(
            isEditMode
              ? "settings.list.nerUpdatedSuccess"
              : "settings.list.nerCreatedSuccess"
          ),
          {
            type: "success",
          }
        );
      } else {
        AppToasty.show(
          t(
            isEditMode
              ? "settings.list.nerUpdateFailed"
              : "settings.list.nerCreationFailed"
          ),
          {
            type: "danger",
          }
        );
      }
      formRef?.current?.resetForm();
      setShowNERModal(false);
      setSelectedIndex(null);
      setSelectedNERId(null);
      setIsEditMode(false);
    },
    [AppToasty, t, isEditMode]
  );

  const nerItemCreationComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          maxHeight: isLargeScreen() ? "95%" : 500,
          height: "auto",
          maxWidth: isLargeScreen() ? 700 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        isModalOpen={showNERModal}
        setIsModalOpen={setShowNERModal}
        modalTitle={
          isEditMode ? t("settings.list.editNER") : t("settings.list.createNER")
        }
        closeModal={() => {
          formRef?.current?.resetForm();
          setShowNERModal(false);
          setSelectedIndex(null);
          setSelectedNERId(null);
        }}
      >
        <SettingsAI
          ref={formRef}
          initialValues={initialMapValues}
          onEntityCreated={onNERCreated}
          isEdit={isEditMode}
          settingType="ner"
          sectionTitle={
            isEditMode ? initialMapValues.label : t("settings.list.newNER")
          }
          opt={
            isEditMode && selectedNERId ? identifyOptByID(selectedNERId) : null
          }
        />
      </ToothFairyModal>
    );
  }, [
    showNERModal,
    setShowNERModal,
    JSON.stringify(initialMapValues),
    isEditMode,
    selectedIndex,
    selectedNERId,
    displayedNERs,
  ]);

  function identifyOptByID(id) {
    return displayedNERs?.find((u) => u.id === id);
  }

  const handleNERItemSelection = ({ id, index }) => {
    setSelectedIndex(index);
    setSelectedNERId(id);
    setIsEditMode(true);
    let extendedItem = identifyOptByID(id);
    setInitialMapValues({
      ...extendedItem,
    });
    setShowNERModal(true);
  };

  const handleNERItemDeletion = async () => {
    try {
      setShowChoice(false);
      const _id = selectedNERId || selectedItem?.id;
      if (_id) {
        await entitiesHelper.deleteEntity(_id);

        const updatedEntities = state?.aiEntitiesConfig?.filter(
          (u) => u.id !== _id
        );
        dispatch("SET_AI_ENTITIES_CONFIG", updatedEntities);

        AppToasty.show(t("settings.list.nerDeletedSuccess"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedNERId(null);
        setSelectedItem(null);
      } else {
        throw new Error(t("settings.list.noItemSelected"));
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("settings.list.nerDeletionFailed"), {
        type: "danger",
      });
    }
  };

  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedNERIds) {
        await entitiesHelper.deleteEntity(id);
      }
      const updatedEntities = state?.aiEntitiesConfig?.filter(
        (u) => !selectedNERIds.includes(u.id)
      );
      dispatch("SET_AI_ENTITIES_CONFIG", updatedEntities);
      setSelectedNERIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("settings.list.nerDeletedSuccess"), {
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("settings.list.nerDeletionFailed"), {
        type: "danger",
      });
    }
  };

  const handleCreation = () => {
    setInitialMapValues({
      label: "",
      description: "",
      emoji: "",
    });
    setIsEditMode(false);
    setShowNERModal(true);
  };

  const isItemActionable = () => {
    return true;
  };

  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteNERConfirmation")}
        subCaption={t("settings.list.deleteNERConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleNERItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteNERConfirmation"),
        })}
        subCaption={t("settings.list.deleteNERConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedNERIds]);

  return (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      {isLoading ? (
        <ToothFairyLoadingPlaceholder
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible={true}
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
          }}
          caption={t("settings.list.loadingNERs")}
        />
      ) : (
        <>
          {displayedNERs.length > 0 ? (
            <AppTable
              isSelectable
              showPagination
              itemsPerPage={50}
              hideSearch={false}
              searchPlaceholder={t("settings.toolbar.searchNers")}
              onSelectionChange={(selectedItems) => {
                setSelectedNERIds(selectedItems?.map((u) => u?.id) || []);
              }}
              bulkActions={[
                {
                  id: "DELETE",
                  label: t("settings.list.delete"),
                  icon: AppAssets.icons.delete,
                  isDestructive: true,
                },
              ]}
              onBulkActionPress={(id) => {
                if (id === "DELETE" && selectedNERIds?.length > 0)
                  setShowBulkDeleteChoice(true);
              }}
              actionButtons
              mapIconWithStatus={() => {
                return AppAssets.icons.activeModel;
              }}
              mainActionLabel={t("settings.list.view")}
              showDropDownMenuForActions
              onClickMainAction={(item, index) => {
                handleNERItemSelection({
                  id: item?.id,
                  index: index,
                });
              }}
              actionButtonStyle={{
                minWidth: 20,
              }}
              onActionItemPress={async (id, item, index) => {
                if (id === "EDIT") {
                  handleNERItemSelection({
                    id: item?.id,
                    index: index,
                  });
                } else if (id === "DELETE") {
                  setSelectedItem(item);
                  setSelectedIndex(index);
                  setSelectedNERId(item?.id);
                  setShowChoice(true);
                }
              }}
              actions={[
                {
                  label: t("settings.list.edit"),
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: t("settings.list.delete"),
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              isItemActionable={() => isItemActionable()}
              style={{
                zIndex: -1,
                marginTop: 15,
                height: displayedNERs?.length >= 50 ? height - 95 : height - 75,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              headers={columns}
              items={displayedNERs
                ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""))
                ?.map((u) => {
                  return {
                    id: u?.id || " ",
                    emoji: u?.emoji || " ",
                    label: u?.label || " ",
                    description:
                      u?.description ||
                      t("settings.list.noDescriptionProvided"),
                    actions: " ",
                  };
                })}
            >
              <ToothFairyButton
                title={t("settings.toolbar.create")}
                onPress={handleCreation}
              />
            </AppTable>
          ) : (
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: "100%",
                marginVertical: 20,
                marginHorizontal: "auto",
                flex: 1,
              }}
              size={30}
              icon={AppAssets.icons.ner}
              mainCaption={t("settings.list.noNERsFound")}
              subCaption={t("settings.list.noNERsFoundSub")}
              actionLabel={t("settings.toolbar.create")}
              onActionPress={handleCreation}
            />
          )}
        </>
      )}
      {nerItemCreationComponent}
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default NERsList;
